import { canManageTypeSales } from '@/api/core/authorization';
import {
  applyTypeSales,
  readTypeSalesContext,
  readTypeSalesResult,
  type TypeSalesContext,
  type TypeSalesProduct,
  type TypeSalesRequest,
  type TypeSalesResult,
} from '@/api/core/type-sales-transition';
import { isApiError } from '@/api/http/errors';
import { useOperatorSession } from '@/auth/runtime';
import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  DatePicker,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@mgrvtechgroup/admin-ds';
import { useEffect, useRef, useState } from 'react';
import type { PropertyRuntime } from './property-queries';
import {
  commandScope,
  holdCommand,
  pendingCommand,
  settleCommand,
} from './type-sales-command-owner';

const label = (product: TypeSalesProduct) => (product === 'midterm' ? '미드텀' : '장기');
const classification = (value: string | null) =>
  value === 'GENERAL' ? '일반' : value === 'MIDTERM' ? '미드텀' : (value ?? '미설정');
const reasonLabels: Record<string, string> = {
  TYPE_HAS_NO_ROOMS: '유형에 객실 없음',
  ROOM_HAS_NO_UNITS: '객실에 Unit 없음',
  ROOM_NOT_CORE_OWNED: '객실 관리권 확인 필요',
  UNIT_NOT_CORE_OWNED: 'Unit 관리권 확인 필요',
  SALE_TRANSITION_BLOCKED: '현재 판매 전환 차단',
  CURRENT_MIDTERM_SALE_REQUIRED: '현재 미드텀 판매 필요',
  CLASSIFICATION_NOT_CONVERTIBLE: '변경할 수 없는 분류',
};
const sale = (value: TypeSalesContext['scope']['rooms'][number]['sale']) =>
  value
    ? `${label(value.product)} · ${value.channel === 'own_website' ? '자체 홈페이지' : '외부 플랫폼'}`
    : '판매 설정 없음';
const actorId = (value: unknown): string | null => {
  if (!value || typeof value !== 'object') return null;
  const id = (value as { user_id?: unknown }).user_id;
  return typeof id === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
    ? id
    : null;
};
interface Props {
  runtime: PropertyRuntime;
  propertyId: string;
  typeId: string;
  typeName: string;
  roomNames?: Record<string, string>;
  unitNames?: Record<string, string>;
}
export function TypeSalesTransitionSection({
  runtime,
  propertyId,
  typeId,
  typeName,
  roomNames = {},
  unitNames = {},
}: Props) {
  const session = useOperatorSession(runtime.session);
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<TypeSalesProduct>('midterm');
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [reverse, setReverse] = useState(false);
  const [preview, setPreview] = useState<TypeSalesContext | null>(null);
  const [intent, setIntent] = useState<TypeSalesRequest | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const [receipt, setReceipt] = useState<TypeSalesResult | null>(null);
  const [fresh, setFresh] = useState<TypeSalesContext | null>(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [verifiedActor, setVerifiedActor] = useState<string | null>(null);
  const commandKey = commandScope(runtime.apiNamespace, typeId);
  const epoch = useRef(0);
  const runtimeOwner = useRef({ value: runtime, serial: 0 });
  if (runtimeOwner.current.value !== runtime) {
    runtimeOwner.current = { value: runtime, serial: runtimeOwner.current.serial + 1 };
    epoch.current++;
  }
  const authorityOwner = useRef('');
  if (session.status === 'authorized') {
    const nextAuthority = JSON.stringify([
      session.authorization?.revision,
      session.authorization?.role_bits,
      session.authorization?.domain_bits.booking,
      session.deniedDomains,
    ]);
    if (authorityOwner.current && authorityOwner.current !== nextAuthority) epoch.current++;
    authorityOwner.current = nextAuthority;
  }
  const mounted = useRef(true);
  const scope = JSON.stringify([
    runtime.apiNamespace,
    session.generation,
    propertyId,
    typeId,
    runtime.typeSalesTransitionEnabled,
    runtimeOwner.current.serial,
    authorityOwner.current,
    session.status === 'checking' ? 'authorized' : session.status,
  ]);
  const lastScope = useRef(scope);
  if (lastScope.current !== scope) {
    lastScope.current = scope;
    epoch.current++;
  }
  useEffect(() => {
    mounted.current = true;
    const owner = epoch;
    return () => {
      mounted.current = false;
      owner.current++;
    };
  }, []);
  useEffect(() => {
    setOpen(false);
    setPreview(null);
    setIntent(null);
    setUncertain(false);
    setReceipt(null);
    setFresh(null);
    setMessage('');
    setReason('');
    setBusy(false);
    setVerifiedActor(null);
  }, [scope]);
  const permitted =
    runtime.typeSalesTransitionEnabled === true &&
    session.status === 'authorized' &&
    session.authorization !== null &&
    !session.deniedDomains.includes('booking') &&
    canManageTypeSales(session.authorization);
  const current = (ticket: number, generation: number) =>
    mounted.current &&
    epoch.current === ticket &&
    runtime.session.getSnapshot().generation === generation &&
    runtime.session.getSnapshot().status === 'authorized' &&
    runtime.typeSalesTransitionEnabled === true;
  const clear = (text: string) => {
    epoch.current++;
    setPreview(null);
    setIntent(null);
    setUncertain(false);
    setReceipt(null);
    setReason('');
    setMessage(text);
    setBusy(false);
  };
  const verifyActor = async (ticket: number, generation: number) => {
    try {
      const id = actorId(await runtime.client.get<unknown>('users/me'));
      if (!current(ticket, generation)) return null;
      if (!id) {
        setMessage('현재 수행자를 확인할 수 없어 원래 요청 확인과 새 적용을 중단했습니다.');
        return null;
      }
      setVerifiedActor(id);
      return id;
    } catch {
      if (current(ticket, generation))
        setMessage('현재 수행자를 확인할 수 없어 원래 요청 확인과 새 적용을 중단했습니다.');
      return null;
    }
  };
  const authorize = async (ticket: number, generation: number) => {
    await runtime.session.loadCurrentAuthorization();
    if (!current(ticket, generation)) return false;
    const latest = runtime.session.getSnapshot();
    if (
      !latest.authorization ||
      latest.deniedDomains.includes('booking') ||
      !canManageTypeSales(latest.authorization)
    ) {
      clear('현재 객실 판매 변경 권한이 없습니다.');
      return false;
    }
    return true;
  };
  const fail = (
    error: unknown,
    phase: 'preview' | 'apply' | 'result',
    ticket: number,
    generation: number,
  ) => {
    if (!current(ticket, generation)) return;
    if (isApiError(error) && (error.status === 401 || error.status === 403)) {
      clear('현재 권한을 확인할 수 없습니다. 다시 로그인하거나 권한을 확인해 주세요.');
      return;
    }
    if (isApiError(error) && error.status === 409) {
      setPreview(null);
      setIntent(null);
      setUncertain(false);
      setMessage('현재 대상이 바뀌었습니다. 다시 미리 보고 확인해 주세요.');
      return;
    }
    if (
      phase !== 'preview' &&
      ((isApiError(error) &&
        (error.status === 503 ||
          error.status === 404 ||
          error.kind === 'timeout' ||
          error.kind === 'network')) ||
        !isApiError(error))
    ) {
      setUncertain(true);
      setMessage(
        '적용 결과를 확인하지 못했습니다. 같은 요청의 결과를 확인하거나 그대로 재전송해 주세요.',
      );
      return;
    }
    setMessage('현재 내용을 확인하지 못했습니다. 다시 시도해 주세요.');
  };
  const review = async () => {
    if (
      !permitted ||
      busy ||
      intent ||
      pendingCommand(commandKey) ||
      (target === 'midterm' && !date)
    )
      return;
    const ticket = ++epoch.current,
      generation = session.generation;
    setBusy(true);
    setPreview(null);
    setReceipt(null);
    setFresh(null);
    setMessage('');
    try {
      if (!(await authorize(ticket, generation))) return;
      const value = await readTypeSalesContext(
        runtime.client,
        typeId,
        target,
        target === 'midterm' ? date : undefined,
      );
      if (current(ticket, generation)) {
        setPreview(value);
        if (!value.allowed || value.blocked_targets.length)
          setMessage('차단된 대상이 있어 전체 적용이 취소됩니다. 기존 예약은 유지됩니다.');
      }
    } catch (error) {
      fail(error, 'preview', ticket, generation);
    } finally {
      if (current(ticket, generation)) setBusy(false);
    }
  };
  const apply = async (replay = false) => {
    const held = pendingCommand(commandKey);
    if (
      !permitted ||
      busy ||
      (replay ? held?.actorId !== verifiedActor : !!held) ||
      (!replay &&
        (!preview?.allowed ||
          preview.target_product !== target ||
          (preview.first_move_in_date ?? null) !== (target === 'midterm' ? date : null) ||
          preview.blocked_targets.length ||
          !reason.trim() ||
          reason.length > 500 ||
          (target === 'long_term' && !reverse))) ||
      (replay && (!intent || !uncertain || intent !== held?.body))
    )
      return;
    const activePreview = preview;
    const activeIntent = intent;
    let body: TypeSalesRequest;
    if (replay && activeIntent) body = activeIntent;
    else if (!replay && activePreview)
      body = {
        command_id: crypto.randomUUID(),
        target_product: activePreview.target_product,
        channel: 'own_website',
        first_move_in_date: activePreview.first_move_in_date,
        reverse_confirmed: activePreview.target_product === 'long_term',
        reason: reason.trim(),
        expected_scope: activePreview.scope,
        expected_scope_fingerprint: activePreview.expected_scope_fingerprint,
      };
    else return;
    const ticket = ++epoch.current,
      generation = session.generation;
    setBusy(true);
    setMessage('');
    try {
      if (!(await authorize(ticket, generation))) return;
      const actor = await verifyActor(ticket, generation);
      if (!actor) return;
      if (replay) {
        if (pendingCommand(commandKey) !== held || held?.actorId !== actor) return;
      } else {
        if (!holdCommand(commandKey, { actorId: actor, body })) return;
        setIntent(body);
      }
      const command = pendingCommand(commandKey);
      const result = await applyTypeSales(runtime.client, typeId, body);
      if (command) settleCommand(commandKey, command);
      if (current(ticket, generation)) {
        setReceipt(result);
        setUncertain(false);
        setPreview(null);
        setMessage('전체 적용 결과가 확인되었습니다. 현재 상태는 다시 조회해 확인하세요.');
      }
    } catch (error) {
      if (isApiError(error) && error.status === 409) {
        const command = pendingCommand(commandKey);
        if (command?.body === body) settleCommand(commandKey, command);
      }
      fail(error, 'apply', ticket, generation);
    } finally {
      if (current(ticket, generation)) setBusy(false);
    }
  };
  const refreshCurrent = async () => {
    if (!permitted || busy || !receipt) return;
    const ticket = ++epoch.current,
      generation = session.generation;
    setBusy(true);
    try {
      if (!(await authorize(ticket, generation))) return;
      const value = await readTypeSalesContext(
        runtime.client,
        typeId,
        intent?.target_product ?? target,
        intent?.target_product === 'midterm' ? (intent.first_move_in_date ?? undefined) : undefined,
      );
      if (current(ticket, generation)) setFresh(value);
    } catch (error) {
      fail(error, 'preview', ticket, generation);
    } finally {
      if (current(ticket, generation)) setBusy(false);
    }
  };
  const checkResult = async () => {
    const held = pendingCommand(commandKey);
    if (!permitted || busy || !intent || !uncertain || held?.body !== intent) return;
    const ticket = ++epoch.current,
      generation = session.generation;
    setBusy(true);
    try {
      if (!(await authorize(ticket, generation))) return;
      if ((await verifyActor(ticket, generation)) !== held.actorId) return;
      const result = await readTypeSalesResult(runtime.client, typeId, intent.command_id);
      settleCommand(commandKey, held);
      if (current(ticket, generation)) {
        setReceipt(result);
        setUncertain(false);
        setPreview(null);
        setMessage('원래 요청의 적용 결과가 확인되었습니다.');
      }
    } catch (error) {
      fail(error, 'result', ticket, generation);
    } finally {
      if (current(ticket, generation)) setBusy(false);
    }
  };
  if (!permitted) return null;
  return (
    <>
      <Button
        variant="outline"
        onClick={() => {
          setOpen(true);
          if (!pendingCommand(commandKey)) {
            if (intent && !receipt) {
              setIntent(null);
              setUncertain(false);
              setPreview(null);
              setMessage(
                '원래 요청의 적용 결과가 확인되었습니다. 현재 상태를 다시 미리 봐 주세요.',
              );
            }
            return;
          }
          const ticket = ++epoch.current;
          const generation = session.generation;
          void verifyActor(ticket, generation).then((actor) => {
            if (!actor || !current(ticket, generation)) return;
            const held = pendingCommand(commandKey);
            if (held) {
              if (held.actorId === actor) {
                setIntent(held.body);
                setUncertain(true);
                setPreview(null);
                setMessage('원래 요청의 결과를 확인하거나 같은 요청을 재전송해 주세요.');
              } else {
                setMessage(
                  '현재 수행자가 원래 요청의 수행자와 달라 이 유형의 변경을 중단했습니다.',
                );
              }
            }
          });
        }}
      >
        판매 분류 일괄 변경
      </Button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            epoch.current++;
            setBusy(false);
            if (intent && !receipt) {
              setUncertain(true);
              setMessage('적용 결과를 확인하지 못했습니다. 같은 요청을 확인해 주세요.');
            } else if (!receipt) {
              setPreview(null);
              setIntent(null);
              setUncertain(false);
            }
          }
        }}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{typeName} 판매 분류 일괄 변경</DialogTitle>
            <DialogDescription>이 유형의 실제 객실과 Unit 전체에 적용합니다.</DialogDescription>
          </DialogHeader>
          <DialogBody className="max-h-[70vh] space-y-4 overflow-y-auto">
            {!intent && (
              <>
                <label className="block">
                  변경할 판매 분류
                  <Select
                    value={target}
                    items={[
                      { value: 'midterm', label: '미드텀' },
                      { value: 'long_term', label: '장기' },
                    ]}
                    onValueChange={(value) => {
                      if (value === 'midterm' || value === 'long_term') {
                        epoch.current++;
                        setBusy(false);
                        setTarget(value);
                        setPreview(null);
                        setMessage('');
                      }
                    }}
                  >
                    <SelectTrigger aria-label="변경할 판매 분류">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="midterm">미드텀</SelectItem>
                      <SelectItem value="long_term">장기</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
                {target === 'midterm' && (
                  <label className="block">
                    첫 입주일
                    <DatePicker
                      value={date ? new Date(`${date}T12:00:00`) : undefined}
                      onChange={(value) => {
                        epoch.current++;
                        setBusy(false);
                        setDate(
                          value
                            ? [
                                value.getFullYear(),
                                String(value.getMonth() + 1).padStart(2, '0'),
                                String(value.getDate()).padStart(2, '0'),
                              ].join('-')
                            : '',
                        );
                        setPreview(null);
                      }}
                    />
                  </label>
                )}
                <Button
                  disabled={busy || !!pendingCommand(commandKey) || (target === 'midterm' && !date)}
                  onClick={() => void review()}
                >
                  영향 미리 보기
                </Button>
              </>
            )}
            {preview && !intent && (
              <section aria-label="변경 영향" className="space-y-3 rounded border p-3">
                <p>
                  전체 객실 {preview.scope.rooms.length}개 · 변경 Unit{' '}
                  {preview.proposed_changes.reduce((n, c) => n + c.changed_unit_ids.length, 0)}개
                </p>
                {preview.scope.rooms.map((room) => (
                  <div key={room.room_id} className="rounded border p-2">
                    <p>
                      객실 {roomNames[room.room_id] ?? '현재 목록 밖 객실'} · 현재 판매:{' '}
                      {sale(room.sale)}
                    </p>
                    <ul>
                      {room.units.map((unit) => (
                        <li key={unit.unit_id}>
                          Unit {unitNames[unit.unit_id] ?? '현재 목록 밖 Unit'}:{' '}
                          {classification(unit.classification)} →{' '}
                          {preview.proposed_changes.some((c) =>
                            c.changed_unit_ids.includes(unit.unit_id),
                          )
                            ? target === 'midterm'
                              ? '미드텀'
                              : '일반'
                            : classification(unit.classification)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                {preview.blocked_targets.map((block, index) => (
                  <p key={index}>
                    차단:{' '}
                    {block.room_id
                      ? (roomNames[block.room_id] ?? '현재 목록 밖 객실')
                      : '객실 유형 전체'}{' '}
                    ·{' '}
                    {block.reason_codes
                      .map((code) => reasonLabels[code] ?? '현재 조건 확인 필요')
                      .join(', ')}
                  </p>
                ))}
                <p>기존 예약의 날짜와 금액은 유지됩니다.</p>
                <p>한 대상이라도 차단되면 전체 적용이 취소됩니다.</p>
                {preview.allowed && preview.blocked_targets.length === 0 && (
                  <>
                    <label className="block">
                      변경 사유
                      <Textarea
                        value={reason}
                        maxLength={500}
                        onChange={(event) => {
                          setReason(event.target.value);
                        }}
                      />
                    </label>
                    {target === 'long_term' && (
                      <label className="flex gap-2">
                        <Checkbox
                          checked={reverse}
                          onCheckedChange={(checked) => {
                            setReverse(checked);
                          }}
                        />
                        장기 판매로 되돌리는 것을 확인했습니다.
                      </label>
                    )}
                    <Button
                      disabled={
                        busy ||
                        !reason.trim() ||
                        reason.length > 500 ||
                        (target === 'long_term' && !reverse)
                      }
                      onClick={() => void apply()}
                    >
                      전체 적용 확인
                    </Button>
                  </>
                )}
              </section>
            )}
            {uncertain && intent && (
              <div className="flex gap-2">
                <Button disabled={busy} onClick={() => void checkResult()}>
                  원래 요청 결과 확인
                </Button>
                <Button variant="outline" disabled={busy} onClick={() => void apply(true)}>
                  같은 요청 재전송
                </Button>
              </div>
            )}
            {receipt && (
              <section role="status">
                <p>적용 확인 · 객실 {receipt.room_receipts.length}개</p>
                <p>
                  유형 차수 {receipt.result_revision} 유지 · 객실 판매 차수:{' '}
                  {receipt.room_receipts.map((r) => r.result_sale_revision).join(', ')}
                </p>
                <Button variant="outline" disabled={busy} onClick={() => void refreshCurrent()}>
                  현재 상태 다시 조회
                </Button>
                {fresh && (
                  <p>
                    현재 조회: 객실 {fresh.scope.rooms.length}개 ·{' '}
                    {fresh.allowed ? '변경 가능' : '변경 차단'}
                  </p>
                )}
              </section>
            )}
            {message && (
              <Alert variant="warning" role="status">
                <AlertDescription>{message}</AlertDescription>
              </Alert>
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
}
